import { useCallback } from 'react'
import { useUser } from 'reactfire'
import { User } from 'firebase'

export interface AuthenticatedFetchOptions extends RequestInit {
  // Extend RequestInit to include all standard fetch options
  skipAuthCheck?: boolean // Allow requests even if not authenticated (useful for public endpoints)
}

export interface AuthenticatedFetchError extends Error {
  status?: number
  statusText?: string
  response?: Response
}

export const useAuthenticatedFetch = () => {
  const user = useUser<User>()

  const authenticatedFetch = useCallback(
    async (url: string, options: AuthenticatedFetchOptions = {}) => {
      const idToken = await user.getIdTokenResult(true)
      const { skipAuthCheck = false, ...fetchOptions } = options

      // Check authentication unless skipAuthCheck is true
      if (!skipAuthCheck && (!user || !idToken.token)) {
        const error = new Error(
          'User is not authenticated'
        ) as AuthenticatedFetchError
        error.status = 401
        throw error
      }

      // Merge the authorization header with any existing headers
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
        ...fetchOptions.headers
      }

      // Add authorization header only if user is authenticated
      if (user && idToken.token) {
        ;(headers as Record<string, string>).Authorization = `${idToken.token}`
      }

      const finalOptions: RequestInit = {
        ...fetchOptions,
        headers
      }

      try {
        const response = await fetch(
          process.env.REACT_APP_AGILEARN_BACKEND_URL + url,
          finalOptions
        )

        if (!response.ok) {
          const error = new Error(
            `HTTP error! status: ${response.status} ${response.statusText}`
          ) as AuthenticatedFetchError
          error.status = response.status
          error.statusText = response.statusText
          error.response = response
          throw error
        }

        return response
      } catch (error) {
        console.error('Authenticated fetch error:', error)
        throw error
      }
    },
    [user]
  )

  const authenticatedFetchJson = useCallback(
    async <T = any>(
      url: string,
      options: AuthenticatedFetchOptions = {}
    ): Promise<T> => {
      const response = await authenticatedFetch(url, options)
      return response.json()
    },
    [authenticatedFetch]
  )

  // Helper method for common GET requests
  const get = useCallback(
    async <T = any>(
      url: string,
      options: Omit<AuthenticatedFetchOptions, 'method'> = {}
    ): Promise<T> => {
      return authenticatedFetchJson<T>(url, { ...options, method: 'GET' })
    },
    [authenticatedFetchJson]
  )

  // Helper method for common POST requests
  const post = useCallback(
    async <T = any>(
      url: string,
      body?: any,
      options: Omit<AuthenticatedFetchOptions, 'method' | 'body'> = {}
    ): Promise<T> => {
      return authenticatedFetchJson<T>(url, {
        ...options,
        method: 'POST',
        body: body ? JSON.stringify(body) : undefined
      })
    },
    [authenticatedFetchJson]
  )

  // Helper method for common PUT requests
  const put = useCallback(
    async <T = any>(
      url: string,
      body?: any,
      options: Omit<AuthenticatedFetchOptions, 'method' | 'body'> = {}
    ): Promise<T> => {
      return authenticatedFetchJson<T>(url, {
        ...options,
        method: 'PUT',
        body: body ? JSON.stringify(body) : undefined
      })
    },
    [authenticatedFetchJson]
  )

  // Helper method for common DELETE requests
  const del = useCallback(
    async <T = any>(
      url: string,
      options: Omit<AuthenticatedFetchOptions, 'method'> = {}
    ): Promise<T> => {
      return authenticatedFetchJson<T>(url, { ...options, method: 'DELETE' })
    },
    [authenticatedFetchJson]
  )

  return {
    authenticatedFetch,
    authenticatedFetchJson,
    get,
    post,
    put,
    delete: del,
    user
  }
}
